import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { apiVideos } from "../api.js";
import { Badge, Card, PageHeader, Table, fmtNum, fmtTime } from "../components/ui.js";

export function VideoDetailPage() {
  const { videoId } = useParams({ from: "/_layout/videos/$videoId" });
  const id = Number(videoId);
  const q = useQuery({ queryKey: ["video", id], queryFn: () => apiVideos.get(id) });

  if (!q.data) return <div className="text-stone-500">Loading…</div>;
  const v = q.data;

  // Build chart data: x = hours since publish, y = views/likes/comments.
  const chartData = v.snapshots.map((s) => ({
    hours: Number(s.hours_since_publish.toFixed(2)),
    type: s.snapshot_type,
    views: s.view_count,
    likes: s.like_count,
    comments: s.comment_count,
  }));

  const milestone24 = v.snapshots.find((s) => s.snapshot_type === "24h");
  const milestone7d = v.snapshots.find((s) => s.snapshot_type === "7d");

  return (
    <>
      <PageHeader
        title={v.title || "(no title)"}
        actions={<a href={v.url} target="_blank" rel="noreferrer" className="text-sm text-stone-700 hover:underline">View on {v.platform} ↗</a>}
      />
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="text-xs text-stone-500 mb-1">Project</div>
          <Link to="/projects/$projectId" params={{ projectId: String(v.project_id) }} className="text-sm hover:underline">
            {v.project_name}
          </Link>
        </Card>
        <Card>
          <div className="text-xs text-stone-500 mb-1">KOC</div>
          {v.koc_id != null ? (
            <Link to="/kocs/$kocId" params={{ kocId: String(v.koc_id) }} className="text-sm hover:underline">
              {v.koc_name ?? `#${v.koc_id}`}
            </Link>
          ) : (
            <span className="text-sm text-stone-400">—</span>
          )}
        </Card>
        <Card>
          <div className="text-xs text-stone-500 mb-1">Published</div>
          <div className="text-sm">{fmtTime(v.publish_time)}</div>
        </Card>
        <Card>
          <div className="text-xs text-stone-500 mb-1">Keyword hits</div>
          <div className="flex flex-wrap gap-1">
            {v.keyword_hits.length === 0
              ? <span className="text-sm text-stone-400">none</span>
              : v.keyword_hits.map((k) => <Badge key={k} tone="green">{k}</Badge>)}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card>
          <div className="text-xs text-stone-500 mb-1">+24h milestone</div>
          {milestone24 ? (
            <div>
              <div className="text-2xl font-semibold tabular-nums">{fmtNum(milestone24.view_count)}</div>
              <div className="text-xs text-stone-500">views @ {milestone24.hours_since_publish.toFixed(1)}h</div>
            </div>
          ) : (
            <div className="text-sm text-stone-400">not captured yet</div>
          )}
        </Card>
        <Card>
          <div className="text-xs text-stone-500 mb-1">+7d milestone</div>
          {milestone7d ? (
            <div>
              <div className="text-2xl font-semibold tabular-nums">{fmtNum(milestone7d.view_count)}</div>
              <div className="text-xs text-stone-500">views @ {milestone7d.hours_since_publish.toFixed(1)}h</div>
            </div>
          ) : (
            <div className="text-sm text-stone-400">not captured yet</div>
          )}
        </Card>
      </div>

      <h2 className="text-lg font-semibold mb-2">Performance over time</h2>
      <Card className="mb-6">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ left: 10, right: 10, top: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hours" tickFormatter={(h) => `${h}h`} />
              <YAxis tickFormatter={(n) => fmtNum(n)} />
              <Tooltip
                formatter={(value: number, name) => [fmtNum(value), name]}
                labelFormatter={(h) => `${h}h since publish`}
              />
              <Line dataKey="views" stroke="#0ea5e9" dot={{ r: 3 }} />
              <Line dataKey="likes" stroke="#10b981" dot={{ r: 3 }} />
              <Line dataKey="comments" stroke="#f59e0b" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <h2 className="text-lg font-semibold mb-2">Snapshots</h2>
      <Table
        rows={v.snapshots}
        columns={[
          { header: "Type", cell: (s) => <Badge tone={s.snapshot_type === "daily" ? "neutral" : "green"}>{s.snapshot_type}</Badge>, className: "w-20" },
          { header: "Captured", cell: (s) => <span className="text-xs text-stone-500">{fmtTime(s.captured_at)}</span> },
          { header: "Hrs", cell: (s) => s.hours_since_publish.toFixed(1), className: "text-right tabular-nums w-16" },
          { header: "Views", cell: (s) => fmtNum(s.view_count), className: "text-right tabular-nums" },
          { header: "Likes", cell: (s) => fmtNum(s.like_count), className: "text-right tabular-nums" },
          { header: "Comments", cell: (s) => fmtNum(s.comment_count), className: "text-right tabular-nums" },
          { header: "Shares", cell: (s) => fmtNum(s.share_count), className: "text-right tabular-nums" },
          { header: "Saves", cell: (s) => fmtNum(s.save_count), className: "text-right tabular-nums" },
        ]}
      />
    </>
  );
}
